import { FaUserGroup } from "react-icons/fa6";
import {
  EventFragment,
  EventsByUserOrganizationIdDocument,
  PublicUserOrganization,
  UpdateEventParticipantsDocument,
  UserGroupFragment,
} from "../../../../__generated__/graphql";
import Avatar from "../../../Avatar";
import ParticipantsSelectionModal from "../../../ParticipantsSelectionModal";
import { useState } from "react";
import { useMutation } from "@apollo/client";
import toast from "react-hot-toast";
import { cn } from "../../../../lib/utils";
import { useEventDetails } from "../../context/hook";
import { UserGroupIcon } from "@heroicons/react/24/outline";
import UserGroupModal from "../../../../pages/projects/pages/project/components/ProjectParticipants/components/UserGroupModal";
import { useUser } from "../../../../contexts/UserContext/hooks/useUser";

export default function ParticipantsInlineList({
  event,
}: {
  event: EventFragment;
}) {
  const [isManageParticipantsModalOpen, setIsManageParticipantsModalOpen] =
    useState(false);
  const [selectedGroupForModal, setSelectedGroupForModal] =
    useState<UserGroupFragment | null>(null);
  const [updateEventParticipants] = useMutation(
    UpdateEventParticipantsDocument
  );
  const { toggleParticipant, isParticipantSelected } = useEventDetails();
  const { currentOrganization } = useUser();

  // Get currently assigned groups
  const currentGroups: UserGroupFragment[] =
    event.eventGroupParticipants
      ?.map((gp) => gp?.userGroup)
      .filter((g): g is UserGroupFragment => g !== undefined && g !== null) ??
    [];

  // User can edit if they're in the same organization that created the group
  const canEditGroup = (group: UserGroupFragment) =>
    currentOrganization?.id === group.organizationId;

  return (
    <>
      <UserGroupModal
        isOpen={!!selectedGroupForModal}
        onClose={() => setSelectedGroupForModal(null)}
        userGroup={selectedGroupForModal}
        readOnly={selectedGroupForModal ? !canEditGroup(selectedGroupForModal) : true}
        projectId={selectedGroupForModal?.projectId ?? event.projectId ?? undefined}
        eventId={selectedGroupForModal?.eventId ?? event.id}
      />

      <ParticipantsSelectionModal
        isOpen={isManageParticipantsModalOpen}
        onClose={() => setIsManageParticipantsModalOpen(false)}
        title="Gérer les participants"
        description="Sélectionnez des participants ou des groupes pour cet évènement."
        initialSelectedUsers={
          event.eventParticipants
            ?.map((participant) => participant?.publicUserOrganization)
            .filter(
              (participant): participant is PublicUserOrganization =>
                participant !== undefined && participant !== null
            ) ?? []
        }
        initialSelectedGroups={currentGroups}
        showGroups={!!event.projectId}
        projectId={event.projectId ?? undefined}
        submitButtonText="Enregistrer"
        onSubmit={async (selectedUserOrganizations, selectedGroups) => {
          if (!event?.id) return;

          try {
            await updateEventParticipants({
              variables: {
                eventId: event?.id,
                participantUserOrganizationIds: selectedUserOrganizations.map(
                  (userOrganization) => userOrganization.id
                ),
                groupParticipantUserGroupIds: selectedGroups?.map((g) => g.id),
              },
              refetchQueries: [
                ...selectedUserOrganizations.map((userOrganization) => ({
                  query: EventsByUserOrganizationIdDocument,
                  variables: { userOrganizationId: userOrganization.id },
                })),
              ],
            });
            setIsManageParticipantsModalOpen(false);
            toast.success("Participants mis à jour");
          } catch (error) {
            toast.error(
              error instanceof Error
                ? error.message
                : "Une erreur est survenue lors de l'ajout des participants"
            );
          }
        }}
      />

      <div className="flex flex-col gap-2">
        <div className="text-sm text-tertiary">
          Ajouter ou filtrer des participants
        </div>

        <div className="flex flex-row items-center">
          {/* Display groups first */}
          {currentGroups.length > 0 && (
            <div className="flex flex-row mr-2">
              {currentGroups.map((group, index) => (
                <button
                  key={group.id}
                  type="button"
                  onClick={() => setSelectedGroupForModal(group)}
                  className={cn(
                    "w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center border border-accent/30 hover:bg-accent/30 hover:border-accent/50 transition-colors cursor-pointer",
                    index > 0 && "-ml-2"
                  )}
                  title={`${group.name} (${group.members?.length ?? 0} membres)`}
                >
                  <UserGroupIcon className="w-4 h-4 text-accent" />
                </button>
              ))}
            </div>
          )}

          {/* Display individual participants */}
          <div className="flex flex-row">
            {event.eventParticipants?.map((participant, index) => {
              if (!participant) return null;

              const isSelected = isParticipantSelected(participant);
              const isFirstAfterGroups = index === 0 && currentGroups.length > 0;

              return (
                <div
                  key={participant.id}
                  className={cn(
                    "cursor-pointer border rounded-full transition-all duration-300",
                    index > 0 && "-ml-2",
                    isFirstAfterGroups && "-ml-2",
                    isSelected
                      ? "border-primary ring-2 ring-primary ring-offset-2 ring-offset-background"
                      : "border-transparent hover:border-primary"
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleParticipant(participant);
                  }}
                >
                  <Avatar
                    userOrganization={participant?.publicUserOrganization}
                    size="medium"
                  />
                </div>
              );
            })}
          </div>

          <div
            data-testid="event-add-participants-button"
            className="ml-2 w-8 h-8 bg-card border border-border rounded-full flex items-center justify-center text-sm text-secondary hover:bg-card/80 cursor-pointer hover:text-accent transition-all duration-300"
            onClick={() => setIsManageParticipantsModalOpen(true)}
          >
            <FaUserGroup className="w-3 h-3" />
            <div className="text-xs">+</div>
          </div>
        </div>
      </div>
    </>
  );
}
